import { EditorContent, EditorContext, useEditor } from "@tiptap/react";
import React, { useEffect } from "react";

// --- Tiptap Core Extensions ---
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";

// --- Custom Extensions ---
import Link from "@tiptap/extension-link";

// --- Styles ---
import "customComponents/AdminComponents/JobForm/Tiptap.scss";

// --- Hooks ---
import { useMobile } from "hooks/use-mobile";
import { useWindowSize } from "hooks/use-window-size";
import { useCursorVisibility } from "hooks/use-cursor-visibility";

// --- UI Primitives ---
import { Spacer } from "components/TiptapUIPrimitive/spacer";
import { Button } from "components/TiptapUIPrimitive/button/index";
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "components/TiptapUIPrimitive/toolbar/index";

// --- Tiptap UI ---
import { HeadingDropdownMenu } from "components/TiptapUI/heading-dropdown/index";
import { ListDropdownMenu } from "components/TiptapUI/list-dropdown-menu/index";
import { NodeButton } from "components/TiptapUI/node-button";
import { MarkButton } from "components/TiptapUI/mark-button";
import {
  HighlightPopover,
  HighlighterButton,
  HighlightContent,
} from "components/TiptapUI/highlight-popover";
import { TextAlignButton } from "components/TiptapUI/text-align-button";
import { LinkPopover, LinkButton, LinkContent } from "components/TiptapUI/link-popover";

// --- Icons ---
import { HighlighterIcon } from "components/TiptapIcons/highlighter-icon";
import { LinkIcon } from "components/TiptapIcons/link-icon";
import { ArrowLeftIcon } from "components/TiptapIcons/arrow-left-icon";
import Placeholder from "@tiptap/extension-placeholder";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import Document from "@tiptap/extension-document";
import OrderedList from "@tiptap/extension-ordered-list";

const MainToolbarContent = ({ onHighlighterClick, onLinkClick, isMobile }) => {
  return (
    <>
      <Spacer />

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4, 5, 6]} />
        <ListDropdownMenu types={["bulletList", "orderedList", "taskList"]} />
        <NodeButton type="codeBlock" />
        <NodeButton type="blockquote" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? <HighlightPopover /> : <HighlighterButton onClick={onHighlighterClick} />}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <Spacer />

      {isMobile && <ToolbarSeparator />}
    </>
  );
};

const MobileToolbarContent = ({ type, onBack }) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? <HighlightContent /> : <LinkContent />}
  </>
);

const TiptapEditor = ({ id, value, onChange, placeholder }) => {
  const editor = useEditor({
    immediatelyRender: false,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Start typing to enter text.",
      },
    },
    extensions: [
      StarterKit,
      Document,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline,
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Image,
      Superscript,
      Subscript,
      Placeholder.configure({
        placeholder,
      }),

      Selection,
      Link.configure({ openOnClick: false }),
      BulletList,
      ListItem,
      OrderedList,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      onChange({ target: { id, value: content } });
    },
  });
  const isMobile = useMobile();
  const windowSize = useWindowSize();
  const [mobileView, setMobileView] = React.useState("main");
  const toolbarRef = React.useRef(null);
  const bodyRect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <div className="border rounded-lg  mt-1">
      <EditorContext.Provider value={{ editor }}>
        <Toolbar
          ref={toolbarRef}
          style={
            isMobile
              ? {
                  bottom: `calc(100% - ${windowSize.height - bodyRect.y}px)`,
                }
              : {}
          }
        >
          {mobileView === "main" ? (
            <MainToolbarContent
              onHighlighterClick={() => setMobileView("highlighter")}
              onLinkClick={() => setMobileView("link")}
              isMobile={isMobile}
            />
          ) : (
            <MobileToolbarContent
              type={mobileView === "highlighter" ? "highlighter" : "link"}
              onBack={() => setMobileView("main")}
            />
          )}
        </Toolbar>

        <div className="content-wrapper">
          <EditorContent editor={editor} role="presentation" className="simple-editor-content" />
        </div>
      </EditorContext.Provider>
    </div>
  );
};

export default TiptapEditor;
